/*
  Scalp Score engine.
  Shared by sections/scalp-score-game.liquid (the quiz) and
  sections/scalp-report.liquid (the results page).

  - QUESTIONS: canonical question + option definitions (ids are stable; labels
    can be overridden by section settings for display only).
  - encode/decode: turn an answers object into a short URL-safe code and back,
    so a report is a shareable link and survives a form redirect.
  - evaluate: a pure function of the answers. Returns the score, the band, the
    penalty breakdown, the stressors (education), the routine (product keys),
    the 14-day plan and the personalized hero copy.

  No DOM access in this file.
*/
(function (w) {
  'use strict';

  var VERSION = 'v1';

  var QUESTIONS = [
    {
      id: 'feel',
      frame: 'MY SCALP FEELS',
      type: 'multi',
      title: 'How does your scalp feel most days?',
      why: 'Your scalp is skin. How it feels tells us what it has been reacting to.',
      options: [
        { id: 'oily', label: 'Oily or greasy by day two', short: 'oily' },
        { id: 'dry', label: 'Dry or tight', short: 'dry' },
        { id: 'flakes', label: 'Flaky', short: 'flaky' },
        { id: 'itchy', label: 'Itchy or irritated', short: 'itchy' },
        { id: 'sensitive', label: 'Sensitive to fragrance or new products', short: 'sensitive' },
        { id: 'balanced', label: 'No complaints', exclusive: true, short: 'fine' }
      ]
    },
    {
      id: 'hair',
      frame: 'MY HAIR IS',
      type: 'single',
      title: 'How would you describe your hair?',
      why: 'Texture changes how fast oil travels down the strand, which changes how often you should wash.',
      options: [
        { id: 'fine', label: 'Fine or straight', short: 'fine' },
        { id: 'medium', label: 'Medium or wavy', short: 'medium' },
        { id: 'thick', label: 'Thick, curly, or coily', short: 'thick or curly' }
      ]
    },
    {
      id: 'cleanse',
      frame: 'I CLEANSE WITH',
      type: 'multi',
      title: 'What is in your cleansing routine right now?',
      why: 'Most cleansers were designed to strip. Your scalp has been compensating for it.',
      options: [
        { id: 'shampoo', label: 'Shampoo', short: 'shampoo' },
        { id: 'conditioner', label: 'Conditioner', short: 'conditioner' },
        { id: 'mask', label: 'Hair mask', short: 'a mask' },
        { id: 'dryshampoo', label: 'Dry shampoo', short: 'dry shampoo' },
        { id: 'cowash', label: 'Co-wash', short: 'co-wash' },
        { id: 'prewash', label: 'Pre-Wash', short: 'Pre-Wash' },
        { id: 'newwash', label: 'New Wash', short: 'New Wash' }
      ]
    },
    {
      id: 'frequency',
      frame: 'I WASH',
      type: 'single',
      title: 'How often do you wash?',
      why: 'Overwashing and underwashing both throw off oil production.',
      options: [
        { id: 'daily', label: 'Daily', short: 'daily' },
        { id: 'eod', label: 'Every other day', short: 'every other day' },
        { id: 'twothree', label: '2 to 3 times a week', short: '2 to 3x a week' },
        { id: 'weekly', label: 'Weekly', short: 'weekly' },
        { id: 'rarely', label: 'Less than weekly', short: 'less than weekly' }
      ]
    },
    {
      id: 'styling',
      frame: 'I STYLE WITH',
      type: 'multi',
      title: 'What is in your style rotation?',
      why: 'Whatever you put in your hair ends up on your scalp.',
      options: [
        { id: 'hairspray', label: 'Hairspray', short: 'hairspray' },
        { id: 'gel', label: 'Gel', short: 'gel' },
        { id: 'hairbalm', label: 'Hair Balm', short: 'Hair Balm' },
        { id: 'wax', label: 'Wax or clay', short: 'wax' },
        { id: 'heat', label: 'Heat, no protectant', short: 'heat' },
        { id: 'heatprotect', label: 'Heat with protectant', short: 'protected heat' },
        { id: 'minimal', label: 'Minimal or air-dry', exclusive: true, short: 'not much' }
      ]
    },
    {
      id: 'lifestyle',
      frame: 'AND I',
      type: 'multi',
      title: 'Anything else your scalp deals with?',
      why: 'Sweat, minerals, and friction all leave residue that shows up as flakes, itch, or dullness.',
      options: [
        { id: 'sweat', label: 'I sweat or work out most days', short: 'sweat most days' },
        { id: 'hardwater', label: 'I have hard water', short: 'have hard water' },
        { id: 'color', label: 'My hair is color-treated', short: 'color my hair' },
        { id: 'hats', label: 'I wear hats or tight styles often', short: 'wear hats a lot' },
        { id: 'stress', label: 'I have been stressed or sleeping poorly', short: 'am stressed' },
        { id: 'none', label: 'None of these', exclusive: true, short: 'am good otherwise' }
      ]
    },
    {
      id: 'goal',
      frame: 'I WANT',
      type: 'single',
      title: 'What would you most like to change?',
      why: 'We tailor your results to this.',
      options: [
        { id: 'lessoil', label: 'Less oil between washes', short: 'less oil' },
        { id: 'calm', label: 'A calmer, less itchy scalp', short: 'a calmer scalp' },
        { id: 'flakes', label: 'No more flakes', short: 'no flakes' },
        { id: 'volume', label: 'More volume and lift', short: 'more volume' },
        { id: 'growth', label: 'Healthier growth, less shedding', short: 'healthier growth' },
        { id: 'balance', label: 'Overall balance and shine', short: 'balance and shine' }
      ]
    },
    {
      id: 'treatment',
      frame: 'SCALP TREATMENTS',
      type: 'single',
      title: 'Have you tried a scalp treatment?',
      why: 'Most treatments add something to the scalp. The ones that work start by clearing it.',
      options: [
        { id: 'using', label: 'I use one now', short: 'use a scalp treatment' },
        { id: 'tried', label: 'I have tried one before', short: 'have tried a scalp treatment' },
        { id: 'never', label: 'Never', short: 'have never tried one' }
      ]
    }
  ];

  function byId(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }
  function has(arr, id) { return (arr || []).indexOf(id) > -1; }

  /* ---------------- encode / decode ---------------- */
  function encode(answers) {
    var parts = QUESTIONS.map(function (q) {
      var picked = answers[q.id] || [];
      return picked.map(function (id) {
        var idx = -1;
        q.options.forEach(function (o, i) { if (o.id === id) idx = i; });
        return idx > -1 ? String(idx) : '';
      }).join('');
    });
    return VERSION + '.' + parts.join('-');
  }

  function decode(code) {
    if (!code || typeof code !== 'string') return null;
    var m = code.match(/^v1\.(.*)$/);
    if (!m) return null;
    var parts = m[1].split('-');
    if (parts.length < 1 || parts.length > QUESTIONS.length) return null;
    var answers = {};
    var any = false;
    QUESTIONS.forEach(function (q, qi) {
      var ids = [];
      (parts[qi] || '').split('').forEach(function (ch) {
        var idx = parseInt(ch, 10);
        if (!isNaN(idx) && q.options[idx]) ids.push(q.options[idx].id);
      });
      if (ids.length) any = true;
      answers[q.id] = ids;
    });
    return any ? answers : null;
  }

  /* ---------------- helpers ---------------- */
  function scoreColor(s) {
    var c1, c2, t;
    if (s <= 50) { c1 = [229, 72, 77]; c2 = [245, 166, 35]; t = s / 50; }
    else { c1 = [245, 166, 35]; c2 = [63, 163, 77]; t = (s - 50) / 50; }
    return 'rgb(' + Math.round(c1[0] + (c2[0] - c1[0]) * t) + ',' +
      Math.round(c1[1] + (c2[1] - c1[1]) * t) + ',' + Math.round(c1[2] + (c2[2] - c1[2]) * t) + ')';
  }

  function band(score) {
    if (score >= 85) return { id: 'balanced', label: 'Balanced', blurb: 'Your routine is protecting your scalp. The plan below keeps it that way.' };
    if (score >= 70) return { id: 'close', label: 'Nearly there', blurb: 'A couple of habits are holding your scalp back from its best.' };
    if (score >= 50) return { id: 'needs', label: 'Needs balance', blurb: 'Your scalp is compensating for your routine. It can reset in a few weeks.' };
    if (score >= 30) return { id: 'stressed', label: 'Under stress', blurb: 'Several things are working against your scalp at once. Fixing the biggest one first makes the rest easier.' };
    return { id: 'overwhelmed', label: 'Overwhelmed', blurb: 'Your scalp is being stripped, coated, and irritated in the same week. Start with the cleanse.' };
  }

  var CATEGORIES = [
    { id: 'cleanse', label: 'Cleansing', max: 42 },
    { id: 'rhythm', label: 'Wash rhythm', max: 18 },
    { id: 'styling', label: 'Styling', max: 30 },
    { id: 'condition', label: 'Scalp condition', max: 31 },
    { id: 'lifestyle', label: 'Lifestyle', max: 19 }
  ];

  /* ---------------- evaluate ---------------- */
  function evaluate(answers) {
    answers = answers || {};
    var feel = answers.feel || [];
    var hair = (answers.hair || [])[0] || 'medium';
    var cleanse = answers.cleanse || [];
    var frequency = (answers.frequency || [])[0] || 'twothree';
    var styling = answers.styling || [];
    var life = answers.lifestyle || [];
    var goal = (answers.goal || [])[0] || 'balance';

    var asked = {};
    QUESTIONS.forEach(function (q) { asked[q.id] = (answers[q.id] || []).length > 0; });

    var r = {
      asked: asked,
      oily: has(feel, 'oily'), dry: has(feel, 'dry'), flakes: has(feel, 'flakes'),
      itchy: has(feel, 'itchy'), sensitive: has(feel, 'sensitive'), balanced: has(feel, 'balanced'),
      hair: hair,
      shampoo: has(cleanse, 'shampoo'), conditioner: has(cleanse, 'conditioner'), mask: has(cleanse, 'mask'),
      dryshampoo: has(cleanse, 'dryshampoo'), cowash: has(cleanse, 'cowash'),
      prewash: has(cleanse, 'prewash'), newwash: has(cleanse, 'newwash'),
      frequency: frequency,
      hairspray: has(styling, 'hairspray'), gel: has(styling, 'gel'), hairbalm: has(styling, 'hairbalm'),
      wax: has(styling, 'wax'), heatNoProtect: has(styling, 'heat'), heatProtect: has(styling, 'heatprotect'),
      minimal: has(styling, 'minimal'),
      sweat: has(life, 'sweat'), hardwater: has(life, 'hardwater'), color: has(life, 'color'),
      hats: has(life, 'hats'), stress: has(life, 'stress'),
      goal: goal,
      treatment: (answers.treatment || [])[0] || ''
    };
    r.complaint = r.oily || r.dry || r.flakes || r.itchy || r.sensitive;
    r.heavyStylers = r.gel || r.hairspray || r.wax;
    r.noCleanser = !r.newwash && !r.shampoo;

    /* ---- penalties (each becomes a stressor card) ---- */
    var pen = [];
    function add(id, cat, pts) { if (pts > 0) pen.push({ id: id, category: cat, points: pts }); }

    if (r.shampoo) add('detergent', 'cleanse', 30);
    if (r.noCleanser) add('nocleanser', 'cleanse', 12);
    if (r.dryshampoo) add('dryshampoo', 'cleanse', 12);

    var freqPen = { daily: 18, eod: 8, twothree: 0, weekly: 5, rarely: 10 }[frequency] || 0;
    if (hair === 'fine' && frequency === 'daily') freqPen = 12;
    if (hair === 'fine' && frequency === 'eod') freqPen = 3;
    if (hair === 'thick' && frequency === 'weekly') freqPen = 0;
    if (hair === 'thick' && frequency === 'rarely') freqPen = 6;
    if (r.sweat && (frequency === 'weekly' || frequency === 'rarely')) freqPen += 4;
    if (freqPen) add(frequency === 'daily' || frequency === 'eod' ? 'overwash' : 'underwash', 'rhythm', freqPen);

    if (r.heatNoProtect) add('heat', 'styling', 12);
    var stylerPts = (r.gel ? 6 : 0) + (r.hairspray ? 6 : 0) + (r.wax ? 6 : 0);
    if (stylerPts) add('stylers', 'styling', stylerPts);

    if (r.oily) add('oily', 'condition', 6);
    if (r.flakes) add('flakes', 'condition', 8);
    if (r.dry) add('dry', 'condition', 6);
    if (r.itchy) add('itchy', 'condition', 7);
    if (r.sensitive) add('sensitive', 'condition', 4);

    if (r.sweat) add('sweat', 'lifestyle', 5);
    if (r.hardwater) add('hardwater', 'lifestyle', 5);
    if (r.hats) add('hats', 'lifestyle', 3);
    if (r.color) add('color', 'lifestyle', 3);
    if (r.stress) add('stress', 'lifestyle', 3);

    var total = pen.reduce(function (s, p) { return s + p.points; }, 0);
    var score = Math.max(0, Math.min(100, 100 - total));
    if (score >= 100 && r.complaint) score = 99;

    var breakdown = CATEGORIES.map(function (c) {
      var lost = pen.filter(function (p) { return p.category === c.id; })
        .reduce(function (s, p) { return s + p.points; }, 0);
      return { id: c.id, label: c.label, lost: Math.min(lost, c.max), max: c.max, note: categoryNote(c.id, lost, r) };
    });

    var stressors = pen
      .filter(function (p) { return p.category !== 'condition'; })
      .sort(function (a, b) { return b.points - a.points; })
      .map(function (p) { return stressorCopy(p, r); });

    var signals = pen.filter(function (p) { return p.category === 'condition'; })
      .map(function (p) { return signalCopy(p.id, r); });

    var formula = pickFormula(r);
    var target = targetRhythm(r);

    return {
      version: VERSION,
      answers: answers,
      routine: r,
      score: score,
      color: scoreColor(score),
      band: band(score),
      breakdown: breakdown,
      stressors: stressors,
      signals: signals,
      scalpType: scalpType(r),
      target: target,
      formula: formula,
      tiles: tiles(r, stressors, target, signals),
      headline: headline(r, stressors, score),
      summary: summary(r, stressors, target, signals),
      summaryParts: summaryParts(r, stressors),
      recs: recommendations(r, formula, stressors),
      plan: plan(r, target, stressors),
      table: table(r, target, stressors, signals),
      signalsNote: signalsNote(signals),
      cleanChapter: stressors.length ? null : cleanChapter(r, target),
      scalpChapter: scalpChapter(r, signals),
      journey: journey(r, stressors),
      impact: impact(r, stressors, formula),
      prewash: prewashPitch(r, stressors, signals),
      newwash: newwashPitch(),
      transition: r.shampoo || r.noCleanser
    };
  }

  /* ---------------- copy generators ---------------- */
  var FREQ_LABEL = { daily: 'daily', eod: 'every other day', twothree: '2 to 3 times a week', weekly: 'weekly', rarely: 'less than weekly' };
  var HAIR_LABEL = { fine: 'fine or straight', medium: 'medium or wavy', thick: 'thick, curly, or coily' };

  function scalpType(r) {
    if (r.balanced || !r.complaint) return 'Balanced';
    if (r.sensitive && !r.oily && !r.dry) return 'Sensitive';
    if (r.oily && r.dry) return 'Combination';
    if (r.oily) return 'Oily-leaning';
    if (r.dry) return 'Dry';
    return 'Reactive';
  }

  function targetRhythm(r) {
    var t = { fine: { id: 'eod', label: 'Every other day' }, medium: { id: 'twothree', label: '2 to 3 times a week' }, thick: { id: 'weekly', label: '1 to 2 times a week' } }[r.hair];
    var ok = r.frequency === t.id || (r.hair === 'medium' && r.frequency === 'twothree') ||
      (r.hair === 'thick' && (r.frequency === 'twothree' || r.frequency === 'weekly')) ||
      (r.hair === 'fine' && (r.frequency === 'eod' || r.frequency === 'twothree'));
    return { id: t.id, label: t.label, onTarget: ok, rinse: r.sweat ? 'plus a water-only rinse after workouts' : '' };
  }

  function pickFormula(r) {
    if (r.sensitive) return { key: 'newwash_ff', name: 'New Wash Fragrance Free', reason: 'You told us your scalp reacts to fragrance and new products. Fragrance Free has the same cleansing base with the essential-oil blend removed, so there is less for a reactive scalp to respond to.' };
    if (r.oily || r.hair === 'fine' || r.dryshampoo || r.sweat || r.heavyStylers) return { key: 'newwash_deep', name: 'New Wash Deep Clean', reason: (r.oily ? 'Your scalp runs oily' : r.dryshampoo || r.heavyStylers ? 'Your routine leaves residue at the root' : r.sweat ? 'You sweat most days' : 'Fine hair shows oil fast') + ', so you need the most thorough cleanse in the range. Deep Clean lifts oil and buildup without detergents, so your scalp is not triggered into producing more.' };
    if (r.dry || r.hair === 'thick') return { key: 'newwash_rich', name: 'New Wash Rich', reason: (r.dry ? 'Your scalp runs dry' : 'Thick and curly hair needs more slip and moisture') + '. Rich has the highest concentration of conditioning oils, so it cleanses while leaving the moisture your scalp barrier needs.' };
    return { key: 'newwash_original', name: 'New Wash Original', reason: 'Your scalp is not leaning oily or dry, so Original gives you the balanced cleanse. It removes what needs to go and keeps the oils your scalp made on purpose.' };
  }

  function categoryNote(id, lost, r) {
    if (!lost) return { cleanse: 'No detergents, no buildup. Good.', rhythm: 'Your wash rhythm suits your hair.', styling: 'Nothing heavy sitting on your scalp.', condition: 'No complaints reported.', lifestyle: 'Nothing extra weighing on your scalp.' }[id];
    return {
      cleanse: r.shampoo ? 'Detergent shampoo is the single biggest drag on your score.' : r.dryshampoo ? 'Dry shampoo is building up between washes.' : 'Your scalp is not getting a true cleanse.',
      rhythm: r.frequency === 'daily' || r.frequency === 'eod' ? 'Washing this often keeps oil production on high alert.' : 'Oil and residue sit too long between washes.',
      styling: r.heatNoProtect ? 'Unprotected heat dries out the scalp along with the hair.' : 'Film-forming stylers settle at the root.',
      condition: 'These are signals, not a verdict. They ease as the routine changes.',
      lifestyle: 'Sweat, minerals, friction, or stress are adding residue or strain.'
    }[id];
  }


  var STRESSOR_EXTRA = {
    detergent: { habit: 'detergent shampoo', tagline: 'The big culprit', effects: ['Oily roots within a day', 'Dry, tight scalp underneath', 'Flakes and itch', 'Flat, over-washed hair', 'Faded color'] },
    nocleanser: { habit: 'skipping a real cleanse', tagline: 'The missing step', effects: ['Dull, limp roots', 'Flakes from trapped buildup', 'Itch that comes and goes', 'Hair that never feels fully clean'] },
    dryshampoo: { habit: 'dry shampoo buildup', tagline: 'The hidden buildup', effects: ['Powdery flakes at the part', 'Itch two days after use', 'Clogged, congested follicles', 'Roots that look clean but feel coated'] },
    overwash: { habit: 'washing too often', tagline: 'The oil loop', effects: ['Oily again within hours', 'Dry scalp underneath the oil', 'Fine hair that goes flat fast', 'Color that fades early'] },
    underwash: { habit: 'going too long between washes', tagline: 'The waiting game', effects: ['Flakes and itch by day four', 'Odor at the scalp', 'Dull, weighed-down roots', 'Congested follicles'] },
    heat: { habit: 'unprotected heat', tagline: 'The dry heat', effects: ['Tight, flaky scalp', 'Brittle hair at the root', 'Frizz that will not settle', 'Slower-looking growth'] },
    stylers: { habit: 'heavy stylers', tagline: 'The film', effects: ['Buildup at the root', 'Itch after styling days', 'Flakes that look like product', 'Hair that resists a clean rinse'] },
    sweat: { habit: 'daily sweat', tagline: 'The salt factor', effects: ['Itch after workouts', 'Tight, irritated skin', 'Odor between washes', 'Breakouts along the hairline'] },
    hardwater: { habit: 'hard water', tagline: 'The mineral film', effects: ['Dull, coated hair', 'Tight scalp after rinsing', 'Cleanser that will not lather', 'Color that fades faster'] },
    hats: { habit: 'hats and friction', tagline: 'The friction factor', effects: ['Irritation at the hairline', 'Sweat trapped against the scalp', 'Flattened, oily roots', 'Tension on the follicle'] },
    color: { habit: 'color processing', tagline: 'The reactive window', effects: ['Sensitivity for weeks after a service', 'Dry, tight scalp', 'Itch from fragrance and detergents', 'Faster fade'] },
    stress: { habit: 'stress and lost sleep', tagline: 'The internal factor', effects: ['Shedding a few months later', 'Oilier scalp under stress', 'Itch that flares with sleep loss', 'Tension across the scalp'] }
  };

  function stressorCopy(p, r) {
    var C = {
      detergent: {
        title: 'Detergent shampoo',
        body: 'Sulfates and other detergents strip the oil your scalp produces to protect itself. Your scalp responds by making more, faster. That is the oily-by-day-two cycle. It also leaves the skin barrier dry enough to flake and itch, which is why one product can cause both problems.',
        fix: 'Give your scalp a break from sulfates and detergents. Within two to three weeks, oil production settles back to normal on its own.'
      },
      nocleanser: {
        title: 'No real cleanse',
        body: 'Conditioner, co-wash, and masks soften hair, but they do not lift sweat, sebum, product, and dead skin off the scalp. Left in place, that layer blocks follicles and reads as dullness, itch, or flakes.',
        fix: 'Put a real cleanse back into the week: something that lifts oil and dead skin off the scalp, not just softens the hair.'
      },
      dryshampoo: {
        title: 'Dry shampoo buildup',
        body: 'Dry shampoo absorbs oil, but nothing carries it away. Starches and aerosol propellants settle at the root, mix with sebum, and clog follicles. Within a few days it looks like flakes and feels like itch.',
        fix: 'Let the root breathe between washes. Use less, use it less often, and make sure wash day clears all of it.'
      },
      overwash: {
        title: r.frequency === 'daily' ? 'Washing every day' : 'Washing more than your hair needs',
        body: 'Every wash removes sebum. When it happens daily, your scalp never reaches the point where it can ease off production. The result is a scalp that feels oily within hours and dry underneath, and hair that gets flatter with every wash.',
        fix: 'Stretch washes gradually. Add one day between washes every week until you land on your target rhythm.'
      },
      underwash: {
        title: 'Going too long between washes',
        body: 'Sebum, sweat, and dead skin cells accumulate on the scalp. After a few days that layer starts to oxidize and feed the yeast that lives on every scalp, which is what produces flakes and itch.' + (r.sweat ? ' Sweating most days shortens that window even more.' : ''),
        fix: 'Wash a little more often, gently, and rinse with water on sweat days.'
      },
      heat: {
        title: 'Heat without protection',
        body: 'Blow dryers and irons pull moisture out of the scalp as well as the hair. Repeated unprotected heat leaves the scalp tight and flaky, and weakens the hair at the root where it is newest.',
        fix: 'Mist a heat protectant before every heat pass and keep the dryer moving at the scalp.'
      },
      stylers: {
        title: 'Heavy stylers at the root',
        body: 'Gels, hairsprays, waxes, and clays form a film. It holds your style, and it also holds sweat and oil against the skin. Over a week that film builds up and blocks follicles unless it is fully removed.',
        fix: 'Keep stylers off the skin where you can, and clear the root fully on wash day.'
      },
      sweat: {
        title: 'Daily sweat',
        body: 'Sweat is mostly water, salt, and urea. As it dries it leaves salt on the scalp, which draws moisture out of the skin and irritates it. Mixed with sebum, it also feeds the microbes that cause itch.',
        fix: 'Rinse with water after every workout, even when you do not cleanse.'
      },
      hardwater: {
        title: 'Hard water minerals',
        body: 'Calcium and magnesium in hard water bind with cleansers and leave a mineral film on skin and hair. On the scalp that film feels tight, looks dull, and makes rinsing less effective.',
        fix: 'Rinse longer than feels necessary, and give the scalp a proper reset once a week.'
      },
      hats: {
        title: 'Friction and trapped heat',
        body: 'Hats and tight styles trap heat and sweat against the scalp and add friction at the hairline. Both irritate the skin and can add tension to follicles over time.',
        fix: 'Loosen styles on rest days and rinse the scalp after long hat days.'
      },
      color: {
        title: 'Color processing',
        body: 'Color services open the cuticle and disturb the scalp barrier. For a few weeks afterward the scalp is more reactive to detergents, fragrance, and heat.',
        fix: 'Go gentle for a month after color: no sulfates, no unprotected heat.'
      },
      stress: {
        title: 'Stress and poor sleep',
        body: 'Cortisol shifts more follicles into the resting phase, which shows up as shedding a couple of months later. It also increases oil production, so stress tends to make an oily scalp oilier.',
        fix: 'Treat scalp massage as part of the routine. It improves circulation and is one of the few habits that helps both stress and the scalp.'
      }
    }[p.id] || { title: p.id, body: '', fix: '' };
    var X = STRESSOR_EXTRA[p.id] || { tagline: '', effects: [] };
    var sentences = C.body.match(/[^.]+\.(\s|$)/g) || [C.body];
    var lead = sentences.slice(0, 2).join('').trim();
    var rest = sentences.slice(2).join('').trim();
    return { id: p.id, category: p.category, points: p.points, title: C.title, habit: X.habit || C.title.toLowerCase(), tagline: X.tagline, lead: lead, body: C.body, rest: rest, effects: X.effects, fix: C.fix };
  }

  function signalCopy(id, r) {
    return {
      oily: { id: id, label: 'Oily by day two', meaning: r.shampoo ? 'Classic rebound: the more you strip, the more your scalp makes.' : 'Your scalp is producing more oil than your routine is removing.' },
      dry: { id: id, label: 'Dry or tight', meaning: 'The moisture barrier is thin. Detergents, heat, and hard water all thin it further.' },
      flakes: { id: id, label: 'Flakes', meaning: 'Usually buildup or barrier damage, not dryness. Adding oil rarely helps; clearing the root does.' },
      itchy: { id: id, label: 'Itch or irritation', meaning: 'The skin is inflamed. Residue, fragrance, and scratching all keep it going.' },
      sensitive: { id: id, label: 'Sensitivity', meaning: 'Your scalp reacts to ingredients. Fewer of them is the fastest fix.' }
    }[id];
  }

  function tiles(r, stressors, target, signals) {
    var top = stressors[0];
    return [
      { id: 'type', label: 'Scalp type', value: scalpType(r), note: r.asked.hair ? HAIR_LABEL[r.hair].charAt(0).toUpperCase() + HAIR_LABEL[r.hair].slice(1) + ' hair' : 'From how it feels' },
      { id: 'stressor', label: 'Biggest stressor', value: top ? top.title : 'None found', note: top ? 'Costing you ' + top.points + ' points' : 'Your routine is clean' },
      { id: 'rhythm', label: 'Wash rhythm', value: FREQ_LABEL[r.frequency].charAt(0).toUpperCase() + FREQ_LABEL[r.frequency].slice(1), note: target.onTarget ? 'On target for your hair' : 'Target: ' + target.label.toLowerCase() },
      { id: 'signals', label: 'Signals flagged', value: signals.length + ' of 5', note: signals.length ? signals.map(function (s) { return s.label; }).join(', ') : 'No complaints reported' }
    ];
  }

  function headline(r, stressors, score) {
    var top = stressors[0] ? stressors[0].id : null;
    if (!stressors.length && !r.complaint) return 'Your scalp is in balance. Keep it there.';
    if (!stressors.length) return 'Your routine is close. Your scalp is asking for a few adjustments.';
    if (score >= 85) return 'Your scalp is in good shape. A few tweaks will make it better.';
    if (top === 'detergent' && r.oily) return 'Your scalp is over-stripped and overcompensating.';
    if (top === 'detergent' && (r.dry || r.flakes || r.itchy)) return 'Your scalp is being stripped faster than it can recover.';
    if (top === 'detergent') return 'Your cleanser is working against your scalp.';
    if (top === 'dryshampoo') return 'Buildup is hiding the problem, not fixing it.';
    if (top === 'nocleanser') return 'Your scalp is soft, but it is not clean.';
    if (top === 'overwash') return 'Your scalp never gets the chance to rebalance.';
    if (top === 'underwash') return 'Oil and residue are sitting on your scalp too long.';
    if (top === 'heat' || top === 'stylers') return 'Your styling routine is landing on your scalp.';
    if (top === 'sweat') return 'Sweat is sitting on your scalp longer than it should.';
    if (top === 'hardwater') return 'Your water is leaving a film on your scalp.';
    if (top === 'stress') return 'Stress is showing up on your scalp.';
    if (top === 'color') return 'Your scalp is still recovering from color.';
    if (top === 'hats') return 'Friction and trapped heat are irritating your scalp.';
    return 'Everyday residue is throwing your scalp off.';
  }

  var GOAL_COPY = {
    lessoil: 'You want less oil between washes. That starts with removing whatever is telling your scalp to make more.',
    calm: 'You want a calmer scalp. That means fewer ingredients and less residue touching the skin.',
    flakes: 'You want the flakes gone. Flakes are almost always buildup or a damaged barrier, so we treat the cause, not the symptom.',
    volume: 'You want more volume. Volume starts at a clean, unweighted root.',
    growth: 'You want healthier growth. Growth depends on clear follicles and a calm, well-supplied scalp.',
    balance: 'You want overall balance. The plan below gets your scalp producing the right amount of oil, then keeps it there.'
  };
  /* which quiz question each stressor came from, so the report can link "edit" */
  var STRESSOR_QUESTION = { detergent: 'cleanse', nocleanser: 'cleanse', dryshampoo: 'cleanse', overwash: 'frequency', underwash: 'frequency', heat: 'styling', stylers: 'styling', sweat: 'lifestyle', hardwater: 'lifestyle', hats: 'lifestyle', color: 'lifestyle', stress: 'lifestyle' };
  function summaryParts(r, stressors) {
    var type = scalpType(r), tl = type.toLowerCase();
    var top = stressors[0];
    return {
      type: type,
      typeLabel: tl === 'balanced' ? 'a balanced scalp' : (/^[aeiou]/.test(tl) ? 'an ' : 'a ') + tl + ' scalp',
      hair: r.asked.hair ? HAIR_LABEL[r.hair] + ' hair' : '',
      freq: FREQ_LABEL[r.frequency],
      count: stressors.length,
      habit: top ? top.habit : '',
      habitQuestion: top ? (STRESSOR_QUESTION[top.id] || 'cleanse') : '',
      goal: (r.asked.goal && GOAL_COPY[r.goal]) || ''
    };
  }

  function summary(r, stressors, target, signals) {
    var parts = [];
    var type = scalpType(r).toLowerCase();
    parts.push('Based on your answers, you have ' + (type === 'balanced' ? 'a balanced scalp' : (/^[aeiou]/.test(type) ? 'an ' : 'a ') + type + ' scalp') +
      (r.asked.hair ? ', ' + HAIR_LABEL[r.hair] + ' hair,' : '') + ' and you wash ' + FREQ_LABEL[r.frequency] + '.');
    if (stressors.length) {
      parts.push((stressors.length === 1 ? 'One thing' : stressors.length + ' things') + ' in your routine ' + (stressors.length === 1 ? 'is' : 'are') + ' working against it' +
        (stressors[0] ? ', and ' + stressors[0].habit + ' is doing most of the damage.' : '.'));
    } else {
      parts.push('Nothing in your routine is actively working against it.');
    }
    var goal = {
      lessoil: 'You want less oil between washes. That starts with removing whatever is telling your scalp to make more.',
      calm: 'You want a calmer scalp. That means fewer ingredients and less residue touching the skin.',
      flakes: 'You want the flakes gone. Flakes are almost always buildup or a damaged barrier, so we treat the cause, not the symptom.',
      volume: 'You want more volume. Volume starts at a clean, unweighted root.',
      growth: 'You want healthier growth. Growth depends on clear follicles and a calm, well-supplied scalp.',
      balance: 'You want overall balance. The plan below gets your scalp producing the right amount of oil, then keeps it there.'
    }[r.goal];
    if (goal && r.asked.goal) parts.push(goal);
    return parts.join(' ');
  }

  /* ---------------- routine ---------------- */
  function recommendations(r, formula, stressors) {
    var recs = [];
    var buildup = r.dryshampoo || r.heavyStylers || r.hardwater || r.sweat || r.flakes || r.itchy || r.oily;

    if (true) {
      recs.push({
        step: 'Step 1', when: 'Once a week, before you cleanse',
        title: r.prewash ? 'Keep Pre-Wash weekly' : 'Reset with Pre-Wash',
        why: r.hardwater ? 'Pre-Wash is a micellar scalp rinse. It lifts the mineral film hard water leaves behind, plus the product and sweat sitting at the root, so your cleanser can actually reach the skin.' :
          r.dryshampoo || r.heavyStylers ? 'Pre-Wash is a micellar scalp rinse that dissolves the film your stylers and dry shampoo leave at the root. Use it before you cleanse on your heaviest wash day.' :
          r.flakes || r.itchy ? 'Pre-Wash is a micellar scalp rinse made to calm an irritated, flaky scalp by clearing what is sitting on it, without scrubbing.' :
          'Pre-Wash is a micellar scalp rinse that loosens oil and buildup so New Wash can do its best work.',
        product: 'prewash'
      });
    }

    recs.push({
      step: recs.length ? 'Step 2' : 'Step 1', when: 'Every wash',
      title: r.newwash ? 'Switch to ' + formula.name : 'Cleanse with ' + formula.name,
      why: (r.shampoo ? 'This is the swap that raises your score the most. New Wash cleanses and conditions in one step with zero detergents, so your scalp stops overproducing oil to compensate. ' : r.noCleanser ? 'This gives you an actual cleanse without going back to detergents. ' : '') + formula.reason,
      product: formula.key
    });

    if (r.newwash && r.prewash && r.complaint) {
      recs.push({
        step: 'Check', when: 'Before you change anything else',
        title: 'Check your technique',
        why: 'You are already on New Wash and Pre-Wash and still have a complaint. Nine times out of ten that is application: not enough product at the scalp, not enough time, or not enough rinsing. Watch this before adding anything.',
        product: 'nw101'
      });
    }

    if (r.flakes || r.itchy || r.dryshampoo || r.heavyStylers || r.hardwater) {
      recs.push({
        step: 'Tool', when: 'Every wash',
        title: 'Massage, don\'t scratch',
        why: 'The Massaging Scalp Brush works cleanser into the scalp and lifts residue without nails. It also boosts circulation, which helps a stressed scalp recover.',
        product: 'brush'
      });
    }

    if (r.dryshampoo || (r.oily && (r.frequency === 'daily' || r.frequency === 'eod'))) {
      recs.push({
        step: 'Between washes', when: 'Day two and three',
        title: 'Stretch washes with Powder',
        why: 'Powder absorbs oil at the root without talc or aerosol, so you can add a day between washes while your scalp learns to make less. Brush it out fully before you cleanse.',
        product: 'powder'
      });
    }

    if (r.heatNoProtect) {
      recs.push({
        step: 'Style', when: 'Before any heat',
        title: 'Protect before heat',
        why: 'Primer is a weightless leave-in mist that shields hair and scalp from heat and detangles as it goes. One pass before the dryer removes 12 points of penalty on its own.',
        product: 'primer'
      });
    }

    if ((r.dry || r.hair === 'thick') && !r.hairbalm && !r.heatNoProtect) {
      recs.push({
        step: 'Style', when: 'On damp hair',
        title: 'Moisturize with Hair Balm',
        why: 'Hair Balm is an air-dry lotion that replenishes moisture and defines texture without a film, so it will not build up at the root the way heavier creams do.',
        product: 'balm'
      });
    }

    if (r.heavyStylers) {
      recs.push({
        step: 'Style', when: 'When you need hold',
        title: 'Style lighter',
        why: 'Water-based stylers rinse away cleanly. Swap your gel, spray, or wax for one that will not leave a film on your scalp between washes.',
        product: 'stylers'
      });
    }

    return recs;
  }

  /* ---------------- 14-day plan ---------------- */
  function plan(r, target, stressors) {
    var items = [];
    if (r.shampoo) items.push({ title: 'Retire the detergent', body: 'Stop using shampoo entirely. Alternating with a gentle cleanser does not work; every detergent wash restarts the rebound.' });
    if (r.noCleanser) items.push({ title: 'Cleanse for real', body: 'Replace co-wash or conditioner-only washes with a cleanser that removes buildup. Your hair will stay soft; your scalp will finally be clean.' });
    if (!target.onTarget) items.push({ title: 'Move toward ' + target.label.toLowerCase(), body: (r.frequency === 'daily' || r.frequency === 'eod') ? 'Add one day between washes each week until you reach your target. Powder covers the in-between days.' : 'Add one wash a week. Your scalp needs residue cleared before it can calm down.' });
    else items.push({ title: 'Keep your wash rhythm', body: 'Your ' + FREQ_LABEL[r.frequency] + ' rhythm suits ' + HAIR_LABEL[r.hair] + ' hair. Do not change it while the rest of the routine settles.' });
    if (r.shampoo || r.noCleanser) items.push({ title: 'Expect an adjustment week', body: 'When you stop stripping, your scalp keeps overproducing for 1 to 3 weeks before it recalibrates. Hair can feel heavier during that window. This is the reset working, not failing.' });
    items.push({ title: 'Work it in, rinse it out', body: 'Apply cleanser at the scalp first, massage for a full minute, then rinse for longer than feels necessary. Most "it did not work" stories are a rinsing problem.' });
    if (r.sweat) items.push({ title: 'Rinse after every workout', body: 'Water only. It takes 30 seconds and removes the salt before it can dry on your scalp.' });
    if (r.flakes || r.itchy) items.push({ title: 'Hands off the scalp', body: 'Scratching breaks the barrier and restarts the itch. Use the brush or fingertips only, and skip anything with fragrance for two weeks.' });
    if (r.heatNoProtect) items.push({ title: 'Protect before heat, every time', body: 'Mist Primer on damp hair and keep the dryer at least six inches from the scalp, always moving.' });
    if (r.heavyStylers) items.push({ title: 'Keep stylers off the skin', body: 'Apply mid-length to ends. Anything that reaches the scalp needs to come off on wash day, so reserve your Pre-Wash for the day after your heaviest style.' });
    if (r.hardwater) items.push({ title: 'Rinse longer', body: 'Hard water slows rinsing. Count to 60 with the water running through your roots, and use Pre-Wash weekly to lift mineral film.' });
    if (r.stress) items.push({ title: 'Two minutes of massage', body: 'On wash days, massage the scalp for two minutes with the brush or fingertips. It increases circulation to the follicle and lowers tension in the scalp muscles.' });
    if (r.color) items.push({ title: 'Go gentle after color', body: 'For four weeks after a color service, no detergents and no unprotected heat. Your barrier is repairing.' });
    items.push({ title: 'Retake the quiz in 30 days', body: 'Your scalp changes faster than you think. Check your score after a month on the new routine.' });
    return items;
  }

  /* ---------------- results table ---------------- */
  var CLEANSER_LABEL = function (r) {
    var parts = [];
    if (r.shampoo) parts.push('Shampoo');
    if (r.newwash) parts.push('New Wash');
    if (r.cowash) parts.push('Co-wash');
    if (r.conditioner && !parts.length) parts.push('Conditioner only');
    if (r.mask && !parts.length) parts.push('Mask only');
    return parts.join(' + ') || 'Nothing';
  };
  function table(r, target, stressors, signals) {
    var rows = [];
    function row(factor, yours, tgt, pts, okLabel) {
      rows.push({ factor: factor, yours: yours, target: tgt, points: pts, delta: pts ? '-' + pts + ' pts' : (okLabel || 'On target'), tone: pts >= 10 ? 'bad' : pts > 0 ? 'warn' : 'good' });
    }
    var det = stressors.filter(function (s) { return s.id === 'detergent'; })[0];
    var noc = stressors.filter(function (s) { return s.id === 'nocleanser'; })[0];
    row('Cleanser', CLEANSER_LABEL(r), 'A cleanser without sulfates', det ? det.points : noc ? noc.points : 0);
    var ds = stressors.filter(function (s) { return s.id === 'dryshampoo'; })[0];
    row('Between washes', r.dryshampoo ? 'Dry shampoo' : 'Nothing', 'Nothing that builds up at the root', ds ? ds.points : 0);
    var rh = stressors.filter(function (s) { return s.id === 'overwash' || s.id === 'underwash'; })[0];
    row('Wash rhythm', FREQ_LABEL[r.frequency].charAt(0).toUpperCase() + FREQ_LABEL[r.frequency].slice(1), target.label + (target.rinse ? ', ' + target.rinse : ''), rh ? rh.points : 0);
    var ht = stressors.filter(function (s) { return s.id === 'heat'; })[0];
    if (r.asked.styling) row('Heat styling', r.heatNoProtect ? 'Unprotected' : r.heatProtect ? 'With protectant' : 'None', 'Protectant before every pass', ht ? ht.points : 0, r.heatProtect ? 'Protected' : 'No heat');
    var st = stressors.filter(function (s) { return s.id === 'stylers'; })[0];
    var stylers = [r.gel && 'Gel', r.hairspray && 'Hairspray', r.wax && 'Wax or clay'].filter(Boolean).join(', ');
    if (r.asked.styling) row('Stylers', stylers || (r.hairbalm ? 'Hair Balm' : 'Minimal'), 'Water-based, off the scalp', st ? st.points : 0, 'Clean');
    var life = stressors.filter(function (s) { return s.category === 'lifestyle'; });
    if (life.length) life.forEach(function (l) { row(l.title, 'Yes', l.fix.split('.')[0], l.points); });
    else if (r.asked.lifestyle) row('Lifestyle', 'Nothing extra', 'Keep it that way', 0, 'Clear');
    if (signals.length) {
      var condPts = { oily: 6, dry: 6, flakes: 8, itchy: 7, sensitive: 4 };
      var cp = signals.reduce(function (a, sg) { return a + (condPts[sg.id] || 0); }, 0);
      row('Scalp signals', signals.map(function (sg) { return sg.label; }).join(', '), 'Calm, balanced, no flakes', cp);
    }
    return rows;
  }
  function signalsNote(signals) {
    var condPts = { oily: 6, dry: 6, flakes: 8, itchy: 7, sensitive: 4 };
    var cp = signals.reduce(function (a, s) { return a + (condPts[s.id] || 0); }, 0);
    return { labels: signals.map(function (s) { return s.label; }), points: cp };
  }

  /* ---------------- scalp journey diagram ---------------- */
  function journey(r, stressors) {
    var top = stressors[0] ? stressors[0].id : (r.complaint ? 'condition' : 'balanced');
    var J = {
      detergent: { chip: 'DETERGENT', nodes: ['Wash day', 'Your scalp', 'Oil rebound', 'Buildup', 'Flakes and itch'], captions: [
        'On wash day, sulfates and detergents in shampoo lift every trace of oil off the scalp, including the layer the skin needs.',
        'Your scalp reads that as an emergency. Sebaceous glands ramp up production to replace what was stripped.',
        'By day two the scalp is oilier than before you washed. That is the rebound, and it is why the cycle feels impossible to break.',
        'Excess oil mixes with dead skin and product residue and settles around the follicle.',
        'That buildup feeds the yeast every scalp carries, which shows up as flakes, itch, and irritation. So you wash again, and the loop restarts.' ] },
      nocleanser: { chip: 'NO CLEANSE', nodes: ['Wash day', 'Your scalp', 'Residue stays', 'Buildup', 'Dull roots'], captions: [
        'Conditioner, co-wash, or a mask soften the hair, but they do not lift sweat, sebum, and dead skin off the scalp.',
        'The scalp keeps producing oil at its normal rate, but nothing carries the old layer away.',
        'Each wash leaves a little more behind. Product film and sebum stack at the root.',
        'Over a few weeks that layer blocks follicles and traps everything the skin sheds.',
        'The result is limp roots, flakes that look like dandruff, and an itch that comes and goes.' ] },
      dryshampoo: { chip: 'DRY SHAMPOO', nodes: ['Day two', 'Your roots', 'Starch and oil', 'Clogged follicles', 'Flakes and itch'], captions: [
        'On day two, dry shampoo goes on to absorb oil at the root and buy another day.',
        'The starch and propellant sit on the scalp. Nothing carries them away until the next full wash.',
        'Absorbed oil, starch, and dead skin combine into a paste around each follicle.',
        'That paste blocks the follicle opening and holds sweat and bacteria against the skin.',
        'Within a few days it looks like flakes and feels like itch, so more dry shampoo goes on, and the cycle repeats.' ] },
      overwash: { chip: 'DAILY WASH', nodes: ['Every morning', 'Your scalp', 'Oil rebound', 'Dry underneath', 'Flat and oily'], captions: [
        'A daily wash removes sebum before the scalp has finished producing it.',
        'The scalp never reaches the point where it can ease off. Production stays on high alert.',
        'By evening the roots are oily again, which makes tomorrow morning feel like it needs a wash.',
        'Underneath that oil, the skin barrier is being stripped every 24 hours and stays dry and tight.',
        'Hair goes flat within hours, the scalp stays reactive, and the daily wash feels non-negotiable.' ] },
      underwash: { chip: 'DAYS BETWEEN', nodes: ['Wash day', 'Day three', 'Oil oxidizes', 'Yeast feeds', 'Flakes and itch'], captions: [
        'Wash day clears the scalp, and for a day or two everything feels balanced.',
        'By day three, sebum, sweat, and dead skin have built a layer over the scalp.' + (r.sweat ? ' Daily sweat shortens that window even more.' : ''),
        'Left in place, that sebum oxidizes and starts to break down on the skin.',
        'The yeast that lives on every scalp feeds on it and multiplies.',
        'That is what produces flakes, itch, and odor by the end of the week.' ] },
      heat: { chip: 'HEAT', nodes: ['Blow dry', 'Your scalp', 'Moisture loss', 'Tight and flaky', 'Brittle roots'], captions: [
        'The dryer or iron passes over the scalp along with the hair, usually at its hottest near the root.',
        'Scalp skin loses water fast under direct heat, and the barrier that holds moisture in thins with every session.',
        'Without a protectant, the hair shaft loses moisture too, starting where it is newest and weakest.',
        'The scalp feels tight the next day and starts to flake as the barrier repairs.',
        'Over months, hair breaks closer to the root and the scalp stays reactive to everything else.' ] },
      stylers: { chip: 'STYLERS', nodes: ['Styling', 'Your scalp', 'The film', 'Trapped oil', 'Buildup and itch'], captions: [
        'Gel, hairspray, wax, or clay goes on to hold the style, and some of it always reaches the scalp.',
        'Those formulas work by forming a film. On the scalp, that film seals over the skin.',
        'Sweat and oil produced during the day are trapped under the film instead of moving down the hair.',
        'Over a week the film thickens unless every wash fully removes it, which detergent-free rinses can struggle with.',
        'Blocked follicles and trapped oil turn into buildup, flakes, and itch that shows up after styling days.' ] },
      sweat: { chip: 'SWEAT', nodes: ['Workout', 'Your scalp', 'Salt dries', 'Irritation', 'Itch and odor'], captions: [
        'A workout leaves the scalp wet with sweat, which is mostly water, salt, and urea.',
        'If it is not rinsed, the water evaporates and leaves salt crystals on the skin.',
        'Salt draws moisture out of the scalp and irritates the barrier.',
        'Mixed with sebum, the residue feeds the microbes that cause itch and odor.',
        'A 30 second water rinse after every workout breaks the cycle before it starts.' ] },
      hardwater: { chip: 'MINERALS', nodes: ['Your water', 'Your scalp', 'Mineral film', 'Weak rinse', 'Dull and tight'], captions: [
        'Hard water carries dissolved calcium and magnesium into the shower.',
        'Those minerals bind with cleansers and with the oils on the scalp.',
        'The result is a mineral film that coats skin and hair after every rinse.',
        'Cleansers lather less and rinse less completely, so residue stays behind.',
        'The scalp feels tight, hair looks dull, and color fades faster. A weekly micellar rinse and a longer rinse fix most of it.' ] },
      hats: { chip: 'FRICTION', nodes: ['Hat on', 'Your scalp', 'Trapped heat', 'Friction', 'Irritation'], captions: [
        'A hat or tight style goes on for hours at a time.',
        'Heat and sweat are trapped against the scalp with nowhere to evaporate.',
        'The skin stays warm and damp, which favors the microbes that cause itch.',
        'At the hairline, friction rubs the barrier thin and adds tension to the follicle.',
        'Irritation and flattened, oily roots follow. Loosen styles on rest days and rinse after long hat days.' ] },
      color: { chip: 'COLOR', nodes: ['Color service', 'Your scalp', 'Open barrier', 'Reactive weeks', 'Recovery'], captions: [
        'A color service opens the cuticle and disturbs the scalp barrier.',
        'For a few weeks the skin is more permeable and more reactive.',
        'Detergents, fragrance, and heat penetrate faster and irritate more.',
        'That is when itch, dryness, and early fade show up.',
        'Gentle, detergent-free cleansing during that window lets the barrier repair and holds color longer.' ] },
      stress: { chip: 'STRESS', nodes: ['Stress', 'Your scalp', 'Cortisol', 'Oil and rest', 'Shedding later'], captions: [
        'Stress and poor sleep raise cortisol in the body.',
        'The scalp responds in two ways: oil glands produce more, and more follicles shift into their resting phase.',
        'More oil means more buildup and a scalp that feels reactive.',
        'Follicles in the resting phase hold the hair for a couple of months, then release it.',
        'That is why shedding shows up about two to three months after a stressful stretch. Scalp massage and a calm routine help both sides.' ] },
      condition: { chip: 'SIGNALS', nodes: ['Your routine', 'Your scalp', 'Small stressors', 'Signals', 'Reset'], captions: [
        'Your routine is close to right, which is why nothing in it stands out as a culprit.',
        'Your scalp is still sending signals, which means something small is adding up.',
        'Technique, rinse time, product amount, and water quality are the usual suspects.',
        'Signals like flakes, oil, or itch are feedback, not a verdict.',
        'A weekly reset with Pre-Wash and a careful, thorough rinse usually clears them within a few weeks.' ] },
      balanced: { chip: 'BALANCE', nodes: ['Cleanse', 'Your scalp', 'Oil in balance', 'Clear follicles', 'Healthy growth'], captions: [
        'A detergent-free cleanse removes what needs to go and leaves the oil the scalp made on purpose.',
        'The scalp never gets the signal to overproduce, so oil stays steady between washes.',
        'Balanced oil coats the hair without collecting at the root.',
        'Follicles stay clear, which is the single biggest factor in how hair grows.',
        'Keep the rhythm, keep the cleanse, and check your score again in a month.' ] }
    };
    var j = J[top] || J.balanced;
    return { key: top, chip: j.chip, nodes: j.nodes, captions: j.captions };
  }

  /* ---------------- scalp chapter (education first, no products) ---------------- */
  function scalpChapter(r, signals) {
    var type = scalpType(r);
    var C = {
      'Dry': { tagline: 'Thin on the surface', lead: 'A dry scalp is skin whose outer barrier has thinned. That barrier is a layer of dead cells held together by the oils your scalp makes, and it is what keeps water in and irritants out. When it thins, moisture escapes and the skin feels tight.', body: 'Dryness on the scalp is rarely a lack of oil. It is usually a barrier that is being worn down faster than it repairs: by detergents, heat, hard water, or simply washing more than the skin can recover from.' },
      'Oily-leaning': { tagline: 'Producing more than it needs', lead: 'Your scalp has thousands of sebaceous glands, and they respond to what happens on the surface. Take oil away and they make more. Leave the surface in balance and they settle down.', body: 'An oily scalp is almost never a dirty scalp. It is a scalp that has been told, over and over, to replace what was stripped. The oil itself is healthy. The problem is when it collects at the root with dead skin and residue, where it feeds the microbes that cause itch and throws the scalp\'s microbiome off balance.' },
      'Combination': { tagline: 'Oily on top, dry underneath', lead: 'Oil at the surface and tightness underneath sound like opposites, but they come from the same loop. When the barrier is stripped, the skin dries out and the glands overproduce to compensate, so you get both at once.', body: 'This is the most common pattern we see, and it is the clearest sign that the routine, not the scalp, is setting the terms.' },
      'Sensitive': { tagline: 'Quick to react', lead: 'A sensitive scalp has a barrier that lets more through. Fragrance, detergents, heat, and even hard water reach the nerve endings faster, which is why it stings, itches, or flushes when other scalps would not notice.', body: 'Under that barrier lives your scalp microbiome, the community of bacteria and fungi that is the skin\'s first line of defense. When it is in balance it calms irritation and keeps the barrier strong. When detergents or fragrance knock it out of balance, everything gets more reactive. Fewer ingredients and less residue will do more for you than any active.' },
      'Reactive': { tagline: 'Sending signals', lead: 'Flakes and itch are your scalp reporting a problem, not causing one. Most of the time the cause is a layer sitting on the skin: oil, dead cells, product, and sweat that never fully clears.', body: 'That layer tips your scalp microbiome out of balance. The yeast every scalp carries feeds on it and multiplies, and the beneficial bacteria that normally keep it in check lose ground. That imbalance is what produces the itch and the flaking. Clear the layer, feed the good bacteria, and the signals fade.' },
      'Balanced': { tagline: 'Doing its job', lead: 'A balanced scalp makes just enough oil to coat the hair and protect the skin, sheds dead cells without them collecting, and keeps its microbiome in check. It is quiet because it is working.', body: 'Balance is easy to lose and easy to keep. The habits in your routine are what keep it here.' }
    }[type];
    var why = {
      oily: 'Oily by day two: your glands are compensating for something that strips them.',
      dry: 'Dry or tight: the barrier is losing water faster than it repairs.',
      flakes: 'Flakes: a layer on the skin, not a lack of moisture.',
      itchy: 'Itch or irritation: the skin is inflamed, usually by residue or an ingredient.',
      sensitive: 'Sensitivity: the barrier is letting more through than it should.'
    };
    var effects = signals.map(function (s) { return why[s.id]; }).filter(Boolean);
    if (!effects.length) effects = ['Oil stays steady between washes', 'Dead skin sheds without collecting', 'The barrier keeps water in and irritants out'];
    return { id: 'scalp', title: 'Your ' + type.toLowerCase() + ' scalp', tagline: C.tagline, lead: C.lead, rest: C.body, effects: effects, contributes: signals.length ? 'What you are feeling, and why:' : 'What a balanced scalp is doing:', points: 0 };
  }

  /* ---------------- perfect-score chapter ---------------- */
  function cleanChapter(r, target) {
    return {
      id: 'clean', title: 'Your routine', tagline: 'Why it is working', points: 0,
      lead: (r.newwash ? 'You cleanse without detergents, so your scalp never gets the signal to overproduce oil. ' : 'Nothing in your routine is stripping your scalp, so oil production stays steady. ') +
        (r.prewash ? 'A weekly Pre-Wash clears the root before you cleanse. ' : '') +
        'And you wash ' + FREQ_LABEL[r.frequency] + ', which suits ' + HAIR_LABEL[r.hair] + ' hair.',
      effects: ['Oil production stays steady between washes', 'Follicles stay clear, which is what growth depends on', 'The barrier stays intact, so nothing itches or flakes'],
      contributes: 'What that protects:',
      fix: 'Keep the cleanse gentle, the rhythm steady, and the root clear. Change one thing at a time if you change anything.'
    };
  }

  /* ---------------- Pre-Wash pitch (the close) ---------------- */
  var PREWASH_IDS = { dryshampoo: 1, stylers: 1, hardwater: 1, sweat: 1, hats: 1, underwash: 1 };
  function prewashPitch(r, stressors, signals) {
    var points = stressors.filter(function (s) { return PREWASH_IDS[s.id]; }).reduce(function (a, s) { return a + s.points; }, 0);
    var condPts = { oily: 6, dry: 0, flakes: 8, itchy: 7, sensitive: 0 };
    signals.forEach(function (s) { points += condPts[s.id] || 0; });
    var reason;
    if (r.hardwater) reason = 'Hard water leaves a film on your scalp that rinsing alone does not remove. Pre-Wash lifts that film and the oil trapped under it so both wash away. It is a micellar rinse, the same idea as micellar water for your face.';
    else if (r.dryshampoo) reason = 'Dry shampoo leaves a layer at the root that a normal wash does not fully clear. Pre-Wash loosens that layer before you cleanse, so the follicle actually gets clean.';
    else if (r.heavyStylers) reason = 'Stylers leave a film that holds sweat and oil against the skin. Pre-Wash breaks that film down before you cleanse, which is the only way to get it off without scrubbing.';
    else if (r.flakes || r.itchy) reason = 'Flakes and itch are almost always a layer sitting on the scalp: dead skin, oil, and residue. Pre-Wash lifts that layer gently so the skin underneath can calm down. It is a prebiotic rinse, so it supports the good bacteria while it clears the rest.';
    else if (r.oily) reason = 'An oily scalp is not a dirty scalp, but oil does trap dead skin and residue at the root. A weekly Pre-Wash clears that without stripping, so your cleanser is not fighting through buildup.';
    else if (r.sweat) reason = 'Sweat leaves salt and oil on the scalp between washes. A weekly Pre-Wash clears what water alone leaves behind.';
    else reason = 'Even a good routine leaves a little behind every week: oil, dead skin, product. Pre-Wash is a five-minute weekly reset that clears the root so everything else works better.';
    var how = [
      'Section dry hair and apply Pre-Wash to the scalp with the applicator, starting at the roots.',
      'Work along each parted section so the whole scalp is covered.',
      'Massage in with the scalp brush or your fingertips and leave it for 3 to 4 minutes.',
      'Wash as you normally do with New Wash.'
    ];
    var works = [
      { title: 'Micelles lift the buildup', text: 'Detergent-free micelles latch onto oil, dirt, and product and rinse it all away without stripping or drying the scalp.' },
      { title: 'Prebiotics rebalance the scalp', text: 'A prebiotic complex feeds the beneficial bacteria your scalp depends on, calming irritation while keeping excess oil in check.' },
      { title: 'Betaine locks in moisture', text: 'Natural, upcycled betaine pulls water into the skin, so the scalp stays hydrated and balanced after you rinse.' }
    ];
    var transition = (r.shampoo || r.noCleanser)
      ? { title: 'Expect an adjustment period', fix: 'A scalp used to detergents keeps overproducing oil for two to three weeks after you stop. That is the reset working, not failing. Pre-Wash shortens it by clearing the excess before you wash, so the scalp settles faster.' }
      : null;
    var FAQ = {
      often: { q: 'How often should I use Pre-Wash?', a: 'Once a week to start. Use it with every wash, or as needed, if you use a lot of styling product, sweat most days, or have hard water.' },
      dry: { q: 'Can I use Pre-Wash if I have a dry scalp?', a: 'Yes. The prebiotics help balance your scalp microbiome, and betaine helps the skin hold moisture and transports water at a cellular level, so a dry scalp ends up nourished, not stripped.' },
      none: { q: 'Can I use Pre-Wash if I do not have any scalp issues?', a: 'Yes. The scalp is constantly collecting dirt, oil, dead skin cells, and product buildup that affects follicle health, and weak follicles mean weaker hair. Pre-Wash clears that so every wash starts clean.' },
      fragrance: { q: 'Is Pre-Wash fragrance-free?', a: 'Yes. Pre-Wash is fragrance-free, so it suits even the most sensitive scalps.' },
      color: { q: 'Is Pre-Wash safe for color-treated hair?', a: 'Yes. The formula is detergent-free, which is what makes it safe for color-treated hair.' },
      who: { q: 'Who can use Pre-Wash?', a: 'Every hair and scalp type. It does the most for people with excess oil, product buildup, or scalp irritation, and it helps with the transition if you have just switched to New Wash from traditional shampoo.' },
      microbiome: { q: 'What is a scalp microbiome?', a: 'The community of bacteria and fungi that live on the scalp. In balance, it is the first line of defense for the follicles: it shields against irritation, damage, and infection and keeps the barrier strong. Out of balance, it shows up as irritation, dryness, sebum buildup, and slower growth.' },
      prebiotics: { q: 'What are prebiotics?', a: 'Ingredients that feed the good bacteria on your scalp. They keep the natural balance in check, which calms redness and itch, and helps the scalp hold moisture and protect itself.' }
    };
    var keys = ['often'];
    if (r.dry) keys.push('dry');
    if (r.sensitive) keys.push('fragrance');
    if (r.color) keys.push('color');
    if (r.flakes || r.itchy) keys.push('prebiotics');
    if (!r.complaint) keys.push('none');
    ['microbiome', 'who'].forEach(function (k) { if (keys.length < 3) keys.push(k); });
    var faq = keys.slice(0, 3).map(function (k) { return FAQ[k]; });
    var context = '';
    if (r.treatment === 'using' && !r.prewash) context = 'You already use a scalp treatment, so you know the scalp needs its own step. The difference here is direction: most treatments add something to the scalp. Pre-Wash clears it first, which is what lets anything else, including yours, actually reach the skin.';
    else if (r.treatment === 'tried' && !r.prewash) context = 'You have tried a scalp treatment before. If it did not stick, this is usually why: most treatments add oils or actives on top of a scalp that has not been cleared. Pre-Wash works the other way around.';
    return { points: points, reason: reason, how: how, works: works, transition: transition, faq: faq, already: r.prewash, context: context, pairLabel: r.prewash ? 'You already use it. Keep it weekly and pair it with' : 'Pair it with' };
  }

  /* ---------------- New Wash: how it works, how to use it (matches the PDP and New Wash 101) ---------------- */
  function newwashPitch() {
    return {
      works: [
        { title: 'Cleanses without detergents', text: 'Essential oils and fatty alcohols bond to dirt, oil, and buildup and rinse them away, without stripping the protective oils your scalp needs.' },
        { title: 'Conditions in the same step', text: 'It replaces shampoo and conditioner, so hair comes out soft and detangled with nothing left coating the scalp.' },
        { title: 'Lets oil production settle', text: 'With nothing stripping the scalp, the glands stop overcompensating and oil levels even out over a few weeks.' }
      ],
      how: [
        'Wet hair and dispense a generous amount, about what you would use for shampoo and conditioner combined. Layer it on from scalp to ends.',
        'Work it into the scalp and through the lengths with the scalp brush or your fingertips. Add a little water for a smooth, milky consistency.',
        'Let it sit for 2 to 3 minutes.',
        'Rinse for longer than feels necessary, massaging as you go, until the water runs clear.'
      ]
    };
  }

  /* ---------------- impact copy ---------------- */
  function impact(r, stressors, formula) {
    var count = stressors.length;
    var lead = count === 0
      ? 'Nothing in your routine is actively working against your scalp. What you do next is about protecting that: keeping the cleanse gentle, the rhythm steady, and the root clear.'
      : count === 1
        ? 'One thing in your routine is working against your scalp. On its own it seems small, but it repeats every wash, and the effects stack: a thinner barrier, oil that will not settle, follicles that stay congested.'
        : count + ' things in your routine are working against your scalp. Individually each seems small, but they repeat every wash and their effects stack: a thinner barrier, oil that will not settle, and follicles that stay congested.';
    var goal = {
      lessoil: 'Less oil between washes starts with removing whatever is telling your scalp to make more.',
      calm: 'A calmer scalp starts with fewer ingredients and less residue touching the skin.',
      flakes: 'Fewer flakes start with clearing the root, not adding moisture on top.',
      volume: 'More volume starts at a clean, unweighted root.',
      growth: 'Healthier growth starts with clear follicles and a scalp that is not inflamed.',
      balance: 'Balance starts with a cleanse that removes buildup without triggering a rebound.'
    }[r.goal] || '';
    if (!r.asked.goal) goal = '';
    var bridge = count ? 'Everything above comes back to one habit: clearing the scalp before you cleanse.' : 'Keeping a scalp balanced comes down to one habit: clearing it before you cleanse.';
    return { lead: lead, goal: goal, bridge: bridge, kicker: 'Start with the reset, not the cleanse.', formulaName: formula.name };
  }

  w.ScalpScore = {
    VERSION: VERSION,
    QUESTIONS: QUESTIONS,
    CATEGORIES: CATEGORIES,
    encode: encode,
    decode: decode,
    evaluate: evaluate,
    scoreColor: scoreColor,
    band: band,
    option: function (qid, oid) { var q = byId(QUESTIONS, qid); return q ? byId(q.options, oid) : null; }
  };
})(window);
